import { IImageStyles, Image, ImageFit } from "@fluentui/react";
import { isAuthRequest } from "components/auth/helpers";
import { useEffect, useState } from "react";
import { STAC_URL, API_VERSION } from "utils/constants";
import { createImageSrc } from "utils/helpers/images";
import { useAuthImageBlob } from "utils/requests";

interface CollectionThumbnailProps {
  collectionId: string;
  assets?: Record<string, any>;
}

export const CatalogCollectionThumbnail = ({
  collectionId,
  assets,
}: CollectionThumbnailProps) => {
  // Check if user provided a thumbnail asset (separate from URL construction)
  const hasUserThumbnail = Boolean(assets?.thumbnail);

  // Construct thumbnail URL only when thumbnail asset exists
  const thumbnailHref = hasUserThumbnail
    ? `${STAC_URL}/collections/${collectionId}/thumbnail?api-version=${API_VERSION}`
    : "";

  const [src, setSrc] = useState<string | undefined>(thumbnailHref);

  const { data: blobSrc } = useAuthImageBlob(thumbnailHref);

  useEffect(() => {
    const fetch = async () => {
      const src = await createImageSrc(collectionId);
      setSrc(src);
    };

    // Use default image when no thumbnail asset is provided
    if (!hasUserThumbnail) {
      fetch();
    } else {
      // Thumbnail asset exists - always perform auth check on the constructed URL
      if (isAuthRequest(thumbnailHref)) {
        setSrc(blobSrc);
      } else {
        setSrc(thumbnailHref);
      }
    }
  }, [collectionId, hasUserThumbnail, thumbnailHref, blobSrc]);

  return (
    <Image
      src={src}
      alt="Dataset thumbnail"
      styles={imageStyles}
      imageFit={ImageFit.cover}
      data-cy="catalog-collection-thumb"
      shouldFadeIn={false}
      loading="lazy"
    />
  );
};

const imageStyles: Partial<IImageStyles> = {
  image: {
    height: 120,
    width: 220,
    borderRadius: 2,
  },
};
